import React, { FC } from 'react';
import classNames from 'classnames';
import getClassNames from 'src/common/utils/getClassNames';
import { withSuffix } from 'src/common/utils/dataAttributeHelpers';
import styles from './Typography.module.scss';

export const alignOptions = [
  'left',
  'center',
  'right',
] as const;
type Align = typeof alignOptions[number];

export const types = [
  'h1',
  'h2',
  'h3',
  'h4',
  'li',
  'p',
  'span',
] as const;
type Type = typeof types[number];

export const sizes = [
  'body-s',
  'body-m',
  'body-l',
  'body-xl',
  'heading-1',
  'heading-2',
  'heading-3',
  'heading-4',
  'heading-5',
  'heading-6',
  'display-1',
  'display-2',
  'display-3',
  'display-4',
  'display-5',
  'display-6',
  'overline',
  'caption',
] as const;
export type Size = typeof sizes[number];

export const weights = [
  'regular',
  'semibold',
  'bold',
] as const;
type Weight = typeof weights[number];

export type TypographyProps = {
  /** Control the alignment of children */
  align?: Align;
  type: Type;
  /** Base size */
  size?: Size;
  /** Optional if using breakpoints. `$xs` and up. Only implemented for display sizes for now. */
  sizeFromBreakpointXS?: Size;
  /** Optional if using breakpoints. `$m` and up */
  sizeFromBreakpointM?: Size;
  /** Optional if using breakpoints. `$l` and up */
  sizeFromBreakpointL?: Size;
  /** Optional if using breakpoints. `$xl` and up */
  sizeFromBreakpointXL?: Size;
  weight?: Weight;
  italic?: boolean;
  className?: string;
  children: React.ReactNode;
  'data-test'?: string;
};
/**
 * This component will reuse styles in the compiled CSS; prefer it to css when possible.
 */
const Typography: FC<TypographyProps> = ({
  align,
  children,
  type: Tag = 'p',
  size = 'body-l',
  sizeFromBreakpointXS,
  sizeFromBreakpointM,
  sizeFromBreakpointL,
  sizeFromBreakpointXL,
  weight,
  italic,
  className,
  'data-test': dataTest,
}) => (
  <Tag
    className={classNames(className, getClassNames(
      styles,
      'root',
      classNames(
        align && `align-${align}`,
        size,
        weight,
        className,
        sizeFromBreakpointXS && `${sizeFromBreakpointXS}_bp-xs`,
        sizeFromBreakpointM && `${sizeFromBreakpointM}_bp-m`,
        sizeFromBreakpointL && `${sizeFromBreakpointL}_bp-l`,
        sizeFromBreakpointXL && `${sizeFromBreakpointXL}_bp-xl`,
        {
          italic,
        },
      ),
    ))}
    data-test={withSuffix(dataTest, 'label')}
  >
    {children}
  </Tag>
);
export default Typography;
